// apps/client/src/components/ExpiringAttachments.tsx

import React from 'react';
import { AttachmentWithContext } from '@fsp/types';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { AttachFile, FileDownload, OpenInNew } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { downloadAttachment } from '../services/attachment';

interface ExpiringAttachmentsProps {
  attachments: AttachmentWithContext[];
  onAttachmentClick?: (attachment: AttachmentWithContext) => void;
}

export const ExpiringAttachments: React.FC<ExpiringAttachmentsProps> = ({
  attachments,
  onAttachmentClick,
}) => {
  const navigate = useNavigate();

  const formatDate = (date?: Date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString();
  };

  const isExpired = (expiryDate?: Date) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const getDaysUntilExpiry = (expiryDate?: Date) => {
    if (!expiryDate) return 0;
    const expiry = new Date(expiryDate);
    const today = new Date();
    return Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleNavigate = (attachment: AttachmentWithContext, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onAttachmentClick) {
      onAttachmentClick(attachment);
    } else if (attachment.navigationUrl) {
      navigate(attachment.navigationUrl);
    }
  };

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      supplier: 'Supplier',
      ccp: 'CCP',
      hazard: 'Hazard',
      processStep: 'Process Step',
      page: 'Page',
      plan: 'Plan',
      organisation: 'Organization',
    };
    return labels[type] || type;
  };

  if (attachments.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No expiring or expired documents
        </Typography>
      </Box>
    );
  }

  const expiredAttachments = attachments.filter((a) => isExpired(a.expiryDate));

  return (
    <Box>
      {expiredAttachments.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {expiredAttachments.length} document{expiredAttachments.length !== 1 ? 's have' : ' has'}{' '}
          expired
        </Alert>
      )}

      <Stack spacing={2}>
        {attachments.slice(0, 10).map((attachment) => {
          const expired = isExpired(attachment.expiryDate);
          const daysUntil = getDaysUntilExpiry(attachment.expiryDate);

          return (
            <Box
              key={attachment.id}
              sx={{
                py: 1.5,
                px: 2,
                border: '1px solid',
                borderColor: expired ? 'error.main' : 'warning.main',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                backgroundColor: expired ? 'error.lighter' : 'warning.lighter',
              }}
            >
              {/* Icon */}
              <AttachFile sx={{ color: 'action.active', flexShrink: 0 }} />

              {/* Left side: Label and entity info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium">
                  {attachment.entityName} - {attachment.label}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={getEntityTypeLabel(attachment.entityType)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Uploaded by {attachment.uploadedByName}
                  </Typography>
                </Stack>
              </Box>

              {/* Center: Expiry info */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
                {attachment.issueDate && (
                  <Chip
                    label={`Issue: ${formatDate(attachment.issueDate)}`}
                    size="small"
                    variant="outlined"
                  />
                )}
                <Chip
                  label={
                    expired
                      ? `Expired ${formatDate(attachment.expiryDate)}`
                      : daysUntil === 0
                        ? 'Expires today'
                        : daysUntil === 1
                          ? 'Expires tomorrow'
                          : `Expires in ${daysUntil} days`
                  }
                  size="small"
                  color={expired ? 'error' : 'warning'}
                  variant="filled"
                />
              </Box>

              {/* Right side: Action buttons */}
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                <Tooltip title={`Download ${attachment.documentFilename}`}>
                  <IconButton
                    size="small"
                    onClick={() => downloadAttachment(attachment)}
                    color="primary"
                  >
                    <FileDownload fontSize="small" />
                  </IconButton>
                </Tooltip>
                {attachment.navigationUrl && (
                  <Tooltip title={`Go to ${attachment.entityName}`}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleNavigate(attachment, e)}
                      color="primary"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {attachments.length > 10 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
          Showing 10 of {attachments.length} expiring documents
        </Typography>
      )}
    </Box>
  );
};

export default ExpiringAttachments;
